import React, { useState, useCallback } from 'react';
import { 
  Upload, Download, AlertCircle, CheckCircle, 
  X, Users, Loader2, ChevronDown, ChevronUp,
  Table, MapPin, Play, RefreshCw, User, Baby, Settings,
  FileSpreadsheet, HelpCircle, Heart, Pill, Utensils, Activity
} from 'lucide-react';
import { usePreviewImport, useValidateImport, useExecuteImport } from '../hooks/useImport';
import { importService } from '../services';

const STEPS = {
  UPLOAD: 1,
  MAPPING: 2,
  VALIDATE: 3,
  IMPORT: 4
};

// Field groupings for the UI
const FIELD_GROUPS = {
  parent: {
    label: 'Parent Information',
    icon: User,
    color: 'bg-blue-50 border-blue-200',
    headerColor: 'bg-blue-100 text-blue-800',
    fields: {
      parent_email: { label: 'Email Address *', type: 'email', required: true },
      parent_first_name: { label: 'First Name *', type: 'text', required: true },
      parent_last_name: { label: 'Last Name *', type: 'text', required: true },
      parent_phone: { label: 'Phone Number', type: 'tel', required: false },
      parent_avatar_url: { label: 'Avatar/Photo URL', type: 'url', required: false }
    }
  },
  studentBasic: {
    label: 'Student Basic Info',
    icon: Baby,
    color: 'bg-green-50 border-green-200',
    headerColor: 'bg-green-100 text-green-800',
    fields: {
      student_first_name: { label: 'First Name *', type: 'text', required: true },
      student_last_name: { label: 'Last Name *', type: 'text', required: true },
      student_gender: { label: 'Gender', type: 'text', required: false },
      student_birth_date: { label: 'Birth Date', type: 'date', required: false },
      student_grade: { label: 'Grade/Level', type: 'text', required: false },
      student_photo_url: { label: 'Photo URL', type: 'url', required: false },
      student_status: { label: 'Status', type: 'text', required: false }
    }
  },
  studentMedical: {
    label: 'Medical & Health Info',
    icon: Heart,
    color: 'bg-red-50 border-red-200',
    headerColor: 'bg-red-100 text-red-800',
    description: 'Critical health information for student safety',
    fields: {
      student_medications: { 
        label: 'Medications', 
        type: 'text', 
        required: false,
        icon: Pill,
        description: 'Current medications, dosage, and timing'
      },
      student_allergies: { 
        label: 'Allergies', 
        type: 'text', 
        required: false,
        description: 'Food, environmental, medication allergies'
      },
      student_medical_conditions: { 
        label: 'Medical Conditions', 
        type: 'text', 
        required: false,
        icon: Activity,
        description: 'Diagnoses, chronic conditions, health issues'
      },
      student_special_needs: { 
        label: 'Special Needs / Disabilities', 
        type: 'text', 
        required: false,
        description: 'IEP, 504 plans, learning disabilities'
      },
      student_dietary_restrictions: { 
        label: 'Dietary Restrictions', 
        type: 'text', 
        required: false,
        icon: Utensils,
        description: 'Special diets, feeding requirements'
      }
    }
  },
  studentEmergency: {
    label: 'Emergency Contact',
    icon: Activity,
    color: 'bg-orange-50 border-orange-200',
    headerColor: 'bg-orange-100 text-orange-800',
    fields: {
      emergency_contact_name: { label: 'Emergency Contact Name', type: 'text', required: false },
      emergency_contact_phone: { label: 'Emergency Contact Phone', type: 'tel', required: false },
      student_notes: { label: 'General Notes', type: 'text', required: false }
    }
  }
};

const IMPORT_TYPES = [
  { id: 'parents_and_students', label: 'Parents + Students', description: 'Import parents and their children together in one file' },
  { id: 'parents_only', label: 'Parents Only', description: 'Import just parent accounts without students' },
  { id: 'students_only', label: 'Students Only', description: 'Add students to existing parents' }
];

export default function ParentImport() {
  const [currentStep, setCurrentStep] = useState(STEPS.UPLOAD);
  const [csvContent, setCsvContent] = useState('');
  const [importType, setImportType] = useState('parents_and_students');
  const [previewData, setPreviewData] = useState(null);
  const [mappings, setMappings] = useState({});
  const [validationResult, setValidationResult] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [expandedSections, setExpandedSections] = useState({ medical: true });
  const [showHelp, setShowHelp] = useState(false);

  const previewMutation = usePreviewImport();
  const validateMutation = useValidateImport();
  const importMutation = useExecuteImport();

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleFileUpload = useCallback((event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      const content = e.target.result;
      setCsvContent(content);

      try {
        const result = await previewMutation.mutateAsync({ 
          csvContent: content,
          delimiter: file.name.endsWith('.tsv') ? '\t' : ',',
          importType
        });
        
        setPreviewData(result.preview);
        setMappings(result.suggestedMappings);
        setCurrentStep(STEPS.MAPPING);
      } catch (err) {
        console.error('Preview failed:', err);
      }
    };
    reader.readAsText(file);
  }, [previewMutation, importType]);

  const handleMappingChange = (field, columnIndex) => {
    const value = columnIndex === '' ? undefined : parseInt(columnIndex, 10);
    setMappings(prev => {
      const updated = { ...prev };
      if (value === undefined) {
        delete updated[field];
      } else {
        updated[field] = value;
      }
      return updated;
    });
  };

  const handleValidate = async () => {
    try {
      const result = await validateMutation.mutateAsync({
        csvContent,
        mappings,
        importType
      });
      setValidationResult(result);
      setCurrentStep(STEPS.VALIDATE);
    } catch (err) {
      console.error('Validation failed:', err);
    }
  };

  const handleImport = async (options = {}) => {
    try {
      const result = await importMutation.mutateAsync({
        csvContent,
        mappings,
        options: { importType, ...options }
      });
      setImportResult(result);
      setCurrentStep(STEPS.IMPORT);
    } catch (err) {
      console.error('Import failed:', err);
    }
  };

  const handleReset = () => {
    setCurrentStep(STEPS.UPLOAD);
    setCsvContent('');
    setPreviewData(null);
    setMappings({});
    setValidationResult(null);
    setImportResult(null);
    setExpandedSections({ medical: true });
  };

  const getMappedFieldCount = (groupKey) => {
    const groupFields = Object.keys(FIELD_GROUPS[groupKey].fields);
    return groupFields.filter(field => mappings[field] !== undefined).length;
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {[
        { step: STEPS.UPLOAD, label: 'Upload', icon: Upload },
        { step: STEPS.MAPPING, label: 'Map Columns', icon: MapPin },
        { step: STEPS.VALIDATE, label: 'Validate', icon: CheckCircle },
        { step: STEPS.IMPORT, label: 'Import', icon: Play }
      ].map(({ step, label, icon: Icon }, index) => (
        <React.Fragment key={step}>
          <div className={`flex items-center ${currentStep >= step ? 'text-purple-600' : 'text-gray-400'}`}>
            <div className={`
              w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm
              ${currentStep >= step ? 'bg-purple-100 border-2 border-purple-600' : 'bg-gray-100 border-2 border-gray-300'}
              ${currentStep === step ? 'ring-4 ring-purple-100' : ''}
            `}>
              <Icon size={20} />
            </div>
            <span className="ml-2 font-medium hidden sm:block">{label}</span>
          </div>
          {index < 3 && (
            <div className={`w-12 sm:w-24 h-0.5 mx-2 sm:mx-4 ${currentStep > step ? 'bg-purple-600' : 'bg-gray-300'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  const renderFieldGroup = (groupKey, groupConfig) => {
    const isExpanded = expandedSections[groupKey] !== false;
    const mappedCount = getMappedFieldCount(groupKey);
    const totalCount = Object.keys(groupConfig.fields).length;

    return (
      <div className={`rounded-lg border ${groupConfig.color} overflow-hidden mb-4`}>
        <button
          onClick={() => toggleSection(groupKey)}
          className={`w-full px-4 py-3 ${groupConfig.headerColor} font-medium flex items-center justify-between`}
        >
          <div className="flex items-center">
            <groupConfig.icon size={18} className="mr-2" />
            {groupConfig.label}
            <span className="ml-2 text-xs bg-white/50 px-2 py-0.5 rounded-full">
              {mappedCount} / {totalCount} mapped
            </span>
          </div>
          {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </button>
        
        {isExpanded && (
          <div className="p-4 space-y-3">
            {groupConfig.description && (
              <p className="text-sm text-gray-600 mb-3">{groupConfig.description}</p>
            )}
            {Object.entries(groupConfig.fields).map(([field, config]) => (
              <div key={field} className="flex items-center justify-between">
                <label className="flex items-center text-sm">
                  {config.icon && <config.icon size={14} className="mr-1.5 text-gray-400" />}
                  {config.label}
                  {config.required && <span className="text-red-500 ml-1">*</span>}
                </label>
                <select
                  value={mappings[field] !== undefined ? mappings[field] : ''}
                  onChange={(e) => handleMappingChange(field, e.target.value)}
                  className="form-input py-1.5 text-sm w-56"
                >
                  <option value="">-- Not mapped --</option>
                  {previewData?.headers.map((header, index) => (
                    <option key={index} value={index}>
                      Column {index + 1}: {header.length > 25 ? header.slice(0, 25) + '...' : header}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderUploadStep = () => (
    <div className="max-w-3xl mx-auto">
      {/* Import Type Selection */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Settings className="mr-2" size={20} />
          What would you like to import?
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {IMPORT_TYPES.map(type => (
            <button
              key={type.id}
              onClick={() => setImportType(type.id)}
              className={`p-4 rounded-lg border-2 text-left transition-all ${
                importType === type.id 
                  ? 'border-purple-600 bg-purple-50' 
                  : 'border-gray-200 hover:border-purple-300'
              }`}
            >
              <div className="font-medium text-gray-900">{type.label}</div>
              <div className="text-sm text-gray-500 mt-1">{type.description}</div>
            </button>
          ))}
        </div>
      </div>

      {/* File Upload Area */}
      <div className="bg-gradient-to-br from-purple-50 to-fuchsia-50 rounded-2xl p-8 text-center border-2 border-dashed border-purple-200">
        <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm">
          <Upload className="text-purple-600" size={40} />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Upload Your Data</h2>
        <p className="text-gray-600 mb-6">
          Upload a CSV file with {importType === 'parents_and_students' ? 'parent and student' : importType === 'parents_only' ? 'parent' : 'student'} information. 
          We&apos;ll help you map the columns and validate everything before importing.
        </p>
        
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
          <label className="btn-primary flex items-center justify-center space-x-2 cursor-pointer">
            <Upload size={20} />
            <span>Choose CSV File</span>
            <input 
              type="file" 
              accept=".csv,.txt,.tsv" 
              onChange={handleFileUpload}
              className="hidden"
            />
          </label>
          <button 
            onClick={() => importService.downloadTemplate(importType)}
            className="btn-secondary flex items-center justify-center space-x-2"
          >
            <Download size={20} />
            <span>Download Template</span>
          </button>
        </div>

        <div className="text-sm text-gray-500">
          <p>Supported formats: CSV, TSV</p>
          <p className="mt-1">
            {importType === 'parents_and_students' && 'Each row can include one parent and one student (add multiple rows for multiple children)'}
            {importType === 'parents_only' && 'Each row should include parent email, first name, and last name'}
            {importType === 'students_only' && 'Each row should include parent email (to link), and student details'}
          </p>
        </div>
      </div>

      {/* Help Section */}
      <div className="mt-6">
        <button 
          onClick={() => setShowHelp(!showHelp)}
          className="flex items-center text-purple-600 hover:text-purple-700 font-medium"
        >
          <HelpCircle size={18} className="mr-1" />
          {showHelp ? 'Hide Help' : 'Show Import Help'}
          {showHelp ? <ChevronUp size={18} className="ml-1" /> : <ChevronDown size={18} className="ml-1" />}
        </button>
        
        {showHelp && (
          <div className="mt-4 bg-blue-50 rounded-lg p-6 border border-blue-200">
            <h4 className="font-semibold text-blue-900 mb-3">How to prepare your CSV file</h4>
            <ul className="space-y-2 text-sm text-blue-800">
              <li className="flex items-start">
                <CheckCircle size={16} className="mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Required fields:</strong> Email, First Name, Last Name (for both parent and student if importing both)</span>
              </li>
              <li className="flex items-start">
                <CheckCircle size={16} className="mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Multiple children:</strong> If a parent has multiple children, add one row per child with the same parent email</span>
              </li>
              <li className="flex items-start">
                <Heart size={16} className="mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Medical Information:</strong> Include medications, allergies, conditions, special needs, and dietary restrictions for student safety</span>
              </li>
              <li className="flex items-start">
                <CheckCircle size={16} className="mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Dates:</strong> Birth dates can be in any standard format (e.g., 2015-03-15, 03/15/2015, March 15, 2015)</span>
              </li>
              <li className="flex items-start">
                <CheckCircle size={16} className="mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Existing parents:</strong> If a parent email already exists, we&apos;ll link the student to them</span>
              </li>
            </ul>
          </div>
        )}
      </div>

      {previewMutation.isPending && (
        <div className="mt-8 text-center">
          <Loader2 className="animate-spin text-purple-600 mx-auto mb-2" size={32} />
          <p className="text-gray-600">Analyzing your file...</p>
        </div>
      )}

      {previewMutation.isError && (
        <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          <div className="flex items-start space-x-2">
            <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">Failed to parse file</p>
              <p className="text-sm mt-1">{previewMutation.error?.message || 'Please check your CSV format and try again.'}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderMappingStep = () => (
    <div className="max-w-5xl mx-auto">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Map CSV Columns</h3>
              <p className="text-gray-500 text-sm mt-1">
                Match your CSV columns to our fields. Required fields are marked with <span className="text-red-500">*</span>
              </p>
            </div>
            <div className="flex items-center space-x-4 text-sm">
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
                <span className="text-gray-600">
                  {getMappedFieldCount('parent')} parent fields mapped
                </span>
              </div>
              {importType !== 'parents_only' && (
                <>
                  <div className="flex items-center">
                    <div className="w-3 h-3 rounded-full bg-blue-500 mr-2"></div>
                    <span className="text-gray-600">
                      {getMappedFieldCount('studentBasic')} basic fields
                    </span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 rounded-full bg-red-500 mr-2"></div>
                    <span className="text-gray-600">
                      {getMappedFieldCount('studentMedical')} medical fields
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Preview Table */}
        <div className="p-6 bg-gray-50 border-b border-gray-200">
          <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
            <Table size={16} className="mr-2" />
            Preview (first 5 rows of your CSV)
          </h4>
          <div className="overflow-x-auto bg-white rounded-lg border border-gray-200">
            <table className="min-w-full">
              <thead className="bg-gray-100">
                <tr>
                  {previewData?.headers.map((header, index) => (
                    <th key={index} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b whitespace-nowrap">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {previewData?.rows.map((row, rowIndex) => (
                  <tr key={rowIndex} className="border-b border-gray-100 last:border-0">
                    {previewData.headers.map((header, colIndex) => (
                      <td key={colIndex} className="px-4 py-2 text-sm text-gray-700 truncate max-w-xs">
                        {row[header] || <span className="text-gray-400 italic">empty</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {previewData?.totalRows > 5 && (
            <p className="text-xs text-gray-500 mt-2">
              Showing 5 of {previewData.totalRows} rows
            </p>
          )}
        </div>

        {/* Column Mapping */}
        <div className="p-6">
          <h4 className="text-sm font-medium text-gray-700 mb-4 flex items-center">
            <MapPin size={16} className="mr-2" />
            Column Mapping
          </h4>
          
          <div className="space-y-4">
            {/* Parent Fields */}
            {renderFieldGroup('parent', FIELD_GROUPS.parent)}

            {/* Student Fields (only if not parents_only) */}
            {importType !== 'parents_only' && (
              <>
                {renderFieldGroup('studentBasic', FIELD_GROUPS.studentBasic)}
                {renderFieldGroup('studentMedical', FIELD_GROUPS.studentMedical)}
                {renderFieldGroup('studentEmergency', FIELD_GROUPS.studentEmergency)}
              </>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="p-6 border-t border-gray-200 flex justify-between">
          <button onClick={handleReset} className="btn-secondary flex items-center">
            <RefreshCw size={18} className="mr-2" />
            Start Over
          </button>
          <button 
            onClick={handleValidate}
            disabled={validateMutation.isPending}
            className="btn-primary flex items-center space-x-2"
          >
            {validateMutation.isPending ? (
              <>
                <Loader2 className="animate-spin" size={18} />
                <span>Validating...</span>
              </>
            ) : (
              <>
                <CheckCircle size={18} />
                <span>Validate Data</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  const renderValidateStep = () => (
    <div className="max-w-5xl mx-auto">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Validation Results</h3>
          <p className="text-gray-500 text-sm mt-1">
            Review the validation results before importing. Fix any errors or choose to skip invalid rows.
          </p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-6 border-b border-gray-200">
          <div className="bg-green-50 rounded-lg p-4 text-center">
            <div className="text-3xl font-bold text-green-600">
              {validationResult?.summary?.validCount || 0}
            </div>
            <div className="text-sm text-green-700">Valid Rows</div>
          </div>
          <div className="bg-red-50 rounded-lg p-4 text-center">
            <div className="text-3xl font-bold text-red-600">
              {validationResult?.summary?.invalidCount || 0}
            </div>
            <div className="text-sm text-red-700">Invalid Rows</div>
          </div>
          <div className="bg-blue-50 rounded-lg p-4 text-center">
            <div className="text-3xl font-bold text-blue-600">
              {validationResult?.summary?.newParents || 0}
            </div>
            <div className="text-sm text-blue-700">New Parents</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4 text-center">
            <div className="text-3xl font-bold text-gray-600">
              {validationResult?.summary?.totalRows || 0}
            </div>
            <div className="text-sm text-gray-700">Total Rows</div>
          </div>
        </div>

        {/* Valid Rows Preview */}
        {validationResult?.validRows?.length > 0 && (
          <div className="border-b border-gray-200">
            <button
              onClick={() => toggleSection('valid')}
              className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center space-x-2">
                <CheckCircle size={20} className="text-green-500" />
                <span className="font-medium text-gray-900">Valid Rows Preview</span>
                <span className="text-sm text-gray-500">({validationResult.validRows.length} shown)</span>
              </div>
              {expandedSections.valid ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>
            {expandedSections.valid && (
              <div className="p-4 bg-gray-50 overflow-x-auto">
                <table className="min-w-full bg-white rounded-lg">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Parent</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Email</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Student</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Grade</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {validationResult.validRows.map((row, index) => (
                      <tr key={index} className="border-b border-gray-100">
                        <td className="px-3 py-2 text-sm">
                          {row.parentData?.first_name} {row.parentData?.last_name}
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-500">{row.parentData?.email}</td>
                        <td className="px-3 py-2 text-sm">
                          {row.studentData?.first_name} {row.studentData?.last_name}
                        </td>
                        <td className="px-3 py-2 text-sm">{row.studentData?.grade || '-'}</td>
                        <td className="px-3 py-2 text-sm">
                          <span className={`text-xs px-2 py-1 rounded-full ${row.isNewParent ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                            {row.isNewParent ? 'New' : 'Existing'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Invalid Rows */}
        {validationResult?.invalidRows?.length > 0 && (
          <div className="border-b border-gray-200">
            <button
              onClick={() => toggleSection('invalid')}
              className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center space-x-2">
                <AlertCircle size={20} className="text-red-500" />
                <span className="font-medium text-gray-900">Invalid Rows</span>
                <span className="text-sm text-gray-500">({validationResult.invalidRows.length} shown)</span>
              </div>
              {expandedSections.invalid ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>
            {expandedSections.invalid && (
              <div className="p-4 bg-red-50">
                {validationResult.invalidRows.map((row, index) => (
                  <div key={index} className="mb-3 last:mb-0 p-3 bg-white rounded-lg border border-red-200">
                    <div className="flex items-start space-x-2">
                      <span className="text-xs font-mono text-gray-500 bg-gray-100 px-2 py-1 rounded">Row {row.rowIndex}</span>
                      <div className="flex-1">
                        <p className="text-sm text-red-600 font-medium">{row.errors.join(', ')}</p>
                        <p className="text-xs text-gray-500 mt-1 truncate">
                          {Object.values(row.rowData).slice(0, 5).join(', ')}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Import Options */}
        <div className="p-6 border-b border-gray-200 bg-yellow-50">
          <h4 className="font-medium text-yellow-900 mb-3">Import Options</h4>
          <div className="space-y-2">
            <label className="flex items-center">
              <input type="checkbox" className="rounded text-purple-600 mr-2" defaultChecked />
              <span className="text-sm text-yellow-800">Skip invalid rows and continue importing valid ones</span>
            </label>
            <label className="flex items-center">
              <input type="checkbox" className="rounded text-purple-600 mr-2" />
              <span className="text-sm text-yellow-800">Update existing parent information if email already exists</span>
            </label>
          </div>
        </div>

        {/* Actions */}
        <div className="p-6 flex justify-between">
          <button onClick={() => setCurrentStep(STEPS.MAPPING)} className="btn-secondary">
            Back to Mapping
          </button>
          <div className="flex space-x-3">
            {validationResult?.hasErrors && (
              <button 
                onClick={() => handleImport({ skipInvalid: true })}
                disabled={importMutation.isPending || validationResult?.summary?.validCount === 0}
                className="btn-secondary"
              >
                Skip Invalid & Import
              </button>
            )}
            <button 
              onClick={() => handleImport({ skipInvalid: false })}
              disabled={importMutation.isPending || validationResult?.hasErrors || validationResult?.summary?.validCount === 0}
              className="btn-primary flex items-center space-x-2"
            >
              {importMutation.isPending ? (
                <>
                  <Loader2 className="animate-spin" size={18} />
                  <span>Importing...</span>
                </>
              ) : (
                <>
                  <Play size={18} />
                  <span>Import {validationResult?.summary?.validCount} Records</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderImportStep = () => (
    <div className="max-w-3xl mx-auto">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
        {importResult?.success ? (
          <>
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="text-green-600" size={40} />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Import Complete!</h2>
              <p className="text-gray-600">{importResult?.message}</p>
            </div>

            {/* Summary Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8">
              <div className="bg-green-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-green-600">{importResult?.summary?.parentsCreated}</div>
                <div className="text-sm text-green-700">Parents Created</div>
              </div>
              {importType !== 'parents_only' && (
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-blue-600">{importResult?.summary?.studentsCreated}</div>
                  <div className="text-sm text-blue-700">Students Created</div>
                </div>
              )}
              {importResult?.summary?.parentsUpdated > 0 && (
                <div className="bg-yellow-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-yellow-600">{importResult?.summary?.parentsUpdated}</div>
                  <div className="text-sm text-yellow-700">Parents Updated</div>
                </div>
              )}
              {importResult?.summary?.skipped > 0 && (
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-gray-600">{importResult?.summary?.skipped}</div>
                  <div className="text-sm text-gray-700">Skipped</div>
                </div>
              )}
            </div>

            {/* Medical Info Notice */}
            {importResult?.results?.students?.created?.some(s => s.hasMedicalInfo) && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <div className="flex items-start space-x-3">
                  <Heart className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
                  <div>
                    <p className="font-medium text-red-900">Medical Information Imported</p>
                    <p className="text-sm text-red-700 mt-1">
                      Some students have medical information (medications, allergies, conditions). 
                      Please review these records carefully to ensure student safety.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Created Parents List */}
            {importResult?.results?.parents?.created?.length > 0 && (
              <div className="mb-6">
                <button
                  onClick={() => toggleSection('createdParents')}
                  className="w-full p-4 flex items-center justify-between bg-gray-50 rounded-lg hover:bg-gray-100 border border-gray-200"
                >
                  <div className="flex items-center">
                    <Users size={18} className="mr-2 text-gray-500" />
                    <span className="font-medium text-gray-900">Created Parents ({importResult.results.parents.created.length})</span>
                  </div>
                  {expandedSections.createdParents ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
                {expandedSections.createdParents && (
                  <div className="mt-2 border border-gray-200 rounded-lg overflow-hidden">
                    <table className="min-w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Name</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Email</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Temp Password</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {importResult.results.parents.created.map((user, index) => (
                          <tr key={index}>
                            <td className="px-4 py-2 text-sm">{user.name}</td>
                            <td className="px-4 py-2 text-sm text-gray-500">{user.email}</td>
                            <td className="px-4 py-2 text-sm font-mono text-purple-600">{user.tempPassword}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Created Students List */}
            {importResult?.results?.students?.created?.length > 0 && (
              <div className="mb-6">
                <button
                  onClick={() => toggleSection('createdStudents')}
                  className="w-full p-4 flex items-center justify-between bg-gray-50 rounded-lg hover:bg-gray-100 border border-gray-200"
                >
                  <div className="flex items-center">
                    <Baby size={18} className="mr-2 text-gray-500" />
                    <span className="font-medium text-gray-900">Created Students ({importResult.results.students.created.length})</span>
                  </div>
                  {expandedSections.createdStudents ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
                {expandedSections.createdStudents && (
                  <div className="mt-2 border border-gray-200 rounded-lg overflow-hidden">
                    <table className="min-w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Name</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Linked Parent</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Medical Info</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {importResult.results.students.created.map((student, index) => (
                          <tr key={index}>
                            <td className="px-4 py-2 text-sm">{student.name}</td>
                            <td className="px-4 py-2 text-sm text-gray-500">{student.parentEmail}</td>
                            <td className="px-4 py-2 text-sm">
                              {student.hasMedicalInfo ? (
                                <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full">Yes</span>
                              ) : (
                                <span className="text-xs text-gray-400">No</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Temp Password Notice */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left mb-6">
              <div className="flex items-start space-x-3">
                <AlertCircle className="text-blue-500 flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <p className="font-medium text-blue-900">Important: Temporary Passwords Generated</p>
                  <p className="text-sm text-blue-700 mt-1">
                    New parent accounts have been created with temporary passwords shown above. 
                    Parents should use <strong>&quot;Forgot Password&quot;</strong> on the login page to set their own secure password.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-center space-x-4">
              <button onClick={handleReset} className="btn-secondary flex items-center space-x-2">
                <RefreshCw size={18} />
                <span>Import More Data</span>
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="text-center">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <X className="text-red-600" size={40} />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Import Failed</h2>
              <p className="text-gray-600 mb-6">
                {importMutation.error?.message || 'Something went wrong during the import.'}
              </p>
              <button onClick={() => setCurrentStep(STEPS.VALIDATE)} className="btn-secondary">
                Back to Validation
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );

  return (
    <div className="animate-fade-in py-8 px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="max-w-4xl mx-auto text-center mb-8">
        <div className="flex items-center justify-center mb-4">
          <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
            <FileSpreadsheet className="text-purple-600" size={28} />
          </div>
        </div>
        <h1 className="text-3xl font-bold text-gray-900">Data Import</h1>
        <p className="text-gray-500 mt-2 max-w-xl mx-auto">
          Migrate your existing parent and student data to ClassroomDragon. 
          Upload a CSV file and we&apos;ll guide you through the process.
        </p>
      </div>

      {renderStepIndicator()}

      {/* Step Content */}
      {currentStep === STEPS.UPLOAD && renderUploadStep()}
      {currentStep === STEPS.MAPPING && renderMappingStep()}
      {currentStep === STEPS.VALIDATE && renderValidateStep()}
      {currentStep === STEPS.IMPORT && renderImportStep()}
    </div>
  );
}
